import { useEffect, useState } from "react";

/**
 * Tiny external store for the global waitlist modal. Lives outside React so any
 * component (including non-hook consumers) can call open()/close() without
 * prop-drilling or context. Open state + last placement are mirrored to
 * sessionStorage so the modal survives page reloads within a tab.
 */
type Listener = (open: boolean, placement: string) => void;

const STORAGE_KEY = "olive:waitlist-modal:v1";

type Persisted = { open: boolean; placement: string };

function readPersisted(): Persisted {
  if (typeof window === "undefined") return { open: false, placement: "unknown" };
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { open: false, placement: "unknown" };
    const parsed = JSON.parse(raw) as Partial<Persisted>;
    return {
      open: parsed.open === true,
      placement: typeof parsed.placement === "string" ? parsed.placement : "unknown",
    };
  } catch {
    return { open: false, placement: "unknown" };
  }
}

function writePersisted(next: Persisted) {
  if (typeof window === "undefined") return;
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
}

const initial = readPersisted();
let isOpen = initial.open;
let placement = initial.placement;
const listeners = new Set<Listener>();

function emit() {
  writePersisted({ open: isOpen, placement });
  listeners.forEach((l) => l(isOpen, placement));
}

export const waitlistModal = {
  open(p: string) {
    placement = p;
    isOpen = true;
    emit();
  },
  close() {
    isOpen = false;
    emit();
  },
  get isOpen() { return isOpen; },
  get placement() { return placement; },
};

export function useWaitlistModal() {
  const [state, setState] = useState({ open: isOpen, placement });
  useEffect(() => {
    const listener: Listener = (open, p) => setState({ open, placement: p });
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }, []);
  return {
    open: state.open,
    placement: state.placement,
    openModal: waitlistModal.open,
    closeModal: waitlistModal.close,
  };
}
